import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Share2, Loader2, GitBranch } from 'lucide-react';
import { ValuePropositionCanvas } from '@/components/canvas/ValuePropositionCanvas';
import { JourneyRail } from '@/components/layout/JourneyRail';
import { GraphPeekPanel } from '@/components/panels/GraphPeekPanel';
import { AssumptionsView } from '@/features/assumption-map/AssumptionsView';
import { MarketResearchView } from '@/features/market-research/MarketResearchView';
import { SynthesisView } from '@/features/synthesis/SynthesisView';
import { CapabilitiesView } from '@/features/capabilities/CapabilitiesView';
import { ThesisAlignmentView } from '@/features/validation/ThesisAlignmentView';
import { DialogView } from '@/features/dialog/DialogView';
import { IdeaView } from '@/features/idea/IdeaView';
import { ProjectMembersDialog } from '@/features/project/ProjectMembersDialog';
import { firstNotDoneStep, JOURNEY_STEP_LIST } from '@/features/project/journey-steps';
import { useStepStatuses } from '@/features/project/step-status';
import { useUIStore } from '@/stores';
import { isViewMode, type ViewMode } from '@/lib/view-mode';
import { useProject } from '@/hooks/use-projects';

// Lazy-loaded so reveal.js (heavy CSS/JS) is code-split out of the initial
// bundle — it only loads when the PM opens the pitch-deck view.
const PitchDeckView = lazy(() =>
  import('@/features/deck/PitchDeckView').then((m) => ({ default: m.PitchDeckView })),
);

export default function ProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = useUIStore((s) => s.viewMode);
  const setViewMode = useUIStore((s) => s.setViewMode);
  const setCanEdit = useUIStore((s) => s.setCanEdit);
  // Every workspace gets the journey rail (the contents page) + graph peek;
  // the full-screen graph is its own place and gets neither.
  const isStepView = viewMode !== 'graph';
  const stableProjectId = useMemo(() => projectId || 'default', [projectId]);
  const projectQuery = useProject(stableProjectId);
  const project = projectQuery.data;
  // useProject resolves to null (a *successful* query, not an error) on a 404 /
  // 403 — a bad link or lost access. Distinguish that settled-but-absent state
  // from "still loading" so the landing gate below doesn't spin forever.
  const projectMissing = !projectQuery.isLoading && !project;
  const [showMembersDialog, setShowMembersDialog] = useState(false);

  // A viewer can't mutate the project — mirror the backend role gate into the
  // UI store so the canvas / node modal go read-only. Default-true elsewhere,
  // so editors/owners are unaffected.
  const callerRole = project?.callerRole;
  const canEdit = callerRole !== 'viewer';
  useEffect(() => {
    setCanEdit(canEdit);
  }, [canEdit, setCanEdit]);

  // Where the Graph toggle returns to: the last non-graph view. A ref (not
  // state) — nothing re-renders on it, it's only read in the click handler.
  // Null only when deep-linking straight to ?view=graph; fall back to step 1.
  const lastNonGraphView = useRef<ViewMode | null>(null);
  useEffect(() => {
    if (viewMode !== 'graph') lastNonGraphView.current = viewMode;
  }, [viewMode]);
  // The step the graph view returns to — drives the destination-labelled exit
  // button and the Graph toggle alike. Reading the ref during render is safe:
  // it can't change while the graph view is the one rendering.
  const graphReturnStep =
    JOURNEY_STEP_LIST.find((s) => s.view === (lastNonGraphView.current ?? 'idea')) ??
    JOURNEY_STEP_LIST[0];

  // Persist the active view in the URL so a refresh (or a shared link) restores
  // it. Hydrate from `?view=` on mount — useLayoutEffect (not useEffect) so the
  // correct view is selected before the first paint. With no (or a stale)
  // param, defer to the landing pick below: the first not-done step, once the
  // status queries have loaded. ProjectPage remounts when you switch projects
  // (navigation routes via the dashboard), so reading the param on mount is
  // enough — no need to re-read it on every render.
  const [landingPending, setLandingPending] = useState(false);
  useLayoutEffect(() => {
    const param = searchParams.get('view');
    if (isViewMode(param)) setViewMode(param);
    else setLandingPending(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The landing pick: deterministic "here's where we are". Same cached reads
  // the rail dots use, so this resolves as soon as the project data lands.
  const statuses = useStepStatuses(stableProjectId);
  useEffect(() => {
    if (!landingPending) return;
    // Resolve on statuses (normal) or give up if the project can't load
    // (projectMissing) — either way stop gating so we don't hang on the spinner.
    if (statuses) {
      setViewMode(firstNotDoneStep(statuses).view);
      setLandingPending(false);
    } else if (projectMissing) {
      setLandingPending(false);
    }
  }, [landingPending, statuses, projectMissing, setViewMode]);

  // Mirror store -> URL on view changes (replace, so flipping views doesn't
  // stack history entries). Skip the first run: hydration above owns the
  // initial value, and mirroring on mount would read the pre-hydration view and
  // clobber a valid incoming `?view=`.
  const didMirrorView = useRef(false);
  useEffect(() => {
    if (!didMirrorView.current) {
      didMirrorView.current = true;
      return;
    }
    if (landingPending) return; // don't stamp the placeholder view into the URL
    if (searchParams.get('view') === viewMode) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', viewMode);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, landingPending]);

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-card">
        <div className="flex items-center gap-3">
          {/* One back button, hierarchical: up a level, destination-labelled.
              Workspaces sit under the project list; the full-screen graph sits
              under the workspace you left (which has the rail for everything
              else). Never two back arrows in the bar. */}
          {viewMode === 'graph' ? (
            <Button variant="ghost" size="sm" onClick={() => setViewMode(graphReturnStep.view)}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to {graphReturnStep.title}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Projects
            </Button>
          )}
          <div className="h-4 w-px bg-border" />
          <h2 className="font-semibold text-sm truncate max-w-[200px]">
            {project?.name || 'Project'}
          </h2>
        </div>

        <div className="flex items-center gap-2">
          {/* Graph is a place, not a step: a top-bar toggle in/out of full-screen
              inspection. From the graph it returns to wherever you came from. */}
          <Button
            variant={viewMode === 'graph' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode(viewMode === 'graph' ? graphReturnStep.view : 'graph')}
          >
            <GitBranch className="h-4 w-4 mr-1" />
            Graph
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowMembersDialog(true)}>
            <Share2 className="h-4 w-4 mr-1" />
            Share
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* The rail navigates, workspaces act: it's the contents page, present
            everywhere except the full-screen graph (inspection). Suppressed
            when the project can't load — there's nothing to navigate. */}
        {isStepView && !projectMissing && (
          <JourneyRail projectId={stableProjectId} landingPending={landingPending} />
        )}
        <div className="flex-1">
          {projectMissing ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center px-6">
              <p className="text-sm text-muted-foreground">
                This project couldn't be found, or you don't have access to it.
              </p>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to projects
              </Button>
            </div>
          ) : landingPending ? (
            // Gate the workspace slot until the landing pick resolves, so we
            // never flash the store's placeholder view.
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
          {viewMode === 'idea' && (
            <IdeaView projectId={stableProjectId} />
          )}
          {viewMode === 'market' && (
            <MarketResearchView projectId={stableProjectId} />
          )}
          {viewMode === 'dialog' && (
            <DialogView projectId={stableProjectId} />
          )}
          {viewMode === 'graph' && (
            <ValuePropositionCanvas projectId={stableProjectId} />
          )}
          {viewMode === 'assumptions' && (
            <AssumptionsView projectId={stableProjectId} />
          )}
          {viewMode === 'synthesis' && (
            <SynthesisView projectId={stableProjectId} />
          )}
          {viewMode === 'capabilities' && (
            <CapabilitiesView projectId={stableProjectId} />
          )}
          {viewMode === 'validation' && (
            <ThesisAlignmentView projectId={stableProjectId} />
          )}
          {viewMode === 'pitch-deck' && (
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <PitchDeckView projectId={stableProjectId} />
            </Suspense>
          )}
            </>
          )}
        </div>

        {/* Owns its collapsed edge-tab state, so it renders on every step view
            regardless of open/closed. */}
        {isStepView && !projectMissing && <GraphPeekPanel projectId={stableProjectId} />}
      </div>

      {/* Mounted only when open so its members query doesn't fire on every
          project load for users who never open Share. */}
      {showMembersDialog && (
        <ProjectMembersDialog
          projectId={stableProjectId}
          isOpen
          onClose={() => setShowMembersDialog(false)}
        />
      )}
    </div>
  );
}
